import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import ShieldIconSVG from '../../../assets/shield-icon.svg';
import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import { ErrorContext } from '../../../ErrorContext';
import { BankConnectTipsModal } from 'src/commons/components/BankConnectTipsModal';
import { SupplementButton } from 'src/commons/components/Button';
import Card, { ErrorCard, LoadingCard } from 'src/commons/components/Card';
import {
  FeatureListItem,
  FeatureListItemBullet,
  FeatureListItemText,
} from 'src/commons/components/FeatureListItem';
import FileDownloadLink from 'src/commons/components/FileDownloadLink';
import Heading from 'src/commons/components/Heading';
import { PlaidLink } from 'src/commons/components/PlaidLink';
import SubHeading from 'src/commons/components/SubHeading';
import {
  ApplicationStatus,
  ApplicationWorkflowEvent,
  CARD_BREAKPOINT,
  colors,
  LegalDocumentContexts,
  MOBILE_BREAKPOINT,
  PageNames,
  UNAUTHORIZED_MESSAGE,
  GraphQLErrorCodes,
} from 'src/commons/constants';
import { GraphQLResponse } from 'src/commons/data/graphql';
import {
  getCreditCardLegalDocuments,
  processCreditCardApplicationWorkflowEvent,
} from 'src/commons/data/queries';
import {
  logError,
  nrTrackCreditCardApplicationWorkflowEvent,
} from 'src/commons/utilities/logging';
import { prefixRoute } from 'src/commons/utilities/routes';

// Types -----------------------------------------------------------------------------------------
type BCADocumentType = {
  name: string;
  title: string;
  version: number;
  viewUrl: string;
};

// #region -- Styles ------------------------------------------------------------------------------
// Style container for displaying Plaid OnExit callback
const BankErrorCard = styled(Card)`
  border-left: 0.3125rem solid ${colors.TEXT_ERROR};
  display: grid;
  grid-template-columns: 2fr 1fr;
  margin-bottom: 0.6875rem;
  min-height: 6rem;
  padding: 1.4375rem 0;
  @media (max-width: ${CARD_BREAKPOINT}) {
    border-left: none;
    border-top: 0.3125rem solid ${colors.TEXT_ERROR};
    display: flex;
    margin-top: 0.625rem;
    padding: 1.3125rem 0 1.5rem;
    width: calc(100% - 1.25rem);
  }
`;
BankErrorCard.displayName = 'BankErrorCard';

const PlaidErrorCard = styled(BankErrorCard)`
  grid-template-columns: unset;
  min-height: 5.25rem;
  padding: 1.125rem 0;
`;
PlaidErrorCard.displayName = 'PlaidErrorCard';

const BankErrorContainer = styled.div`
  line-height: 1.5rem;
  padding-left: 1.6875rem;
  text-align: left;
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin-bottom: 1.25rem;
    padding: 0 1rem;
    text-align: center;
  }
`;
BankErrorContainer.displayName = 'BankErrorContainer';

const PlaidErrorContainer = styled(BankErrorContainer)`
  padding: 0 1rem 0 1.6875rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin: 0;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 1.25rem;
  }
`;
PlaidErrorContainer.displayName = 'PlaidErrorContainer';

const BankErrorHeading = styled.h1`
  color: ${colors.TEXT_PRIMARY};
  font-size: 1.125rem;
  font-weight: 500;
  @media (max-width: ${CARD_BREAKPOINT}) {
    font-size: 1rem;
  }
`;
BankErrorHeading.displayName = 'BankErrorHeading';

const PlaidErrorHeading = styled(BankErrorHeading)`
  font-size: 1rem;
  font-weight: 500;
  text-align: left;
`;
PlaidErrorHeading.displayName = 'PlaidErrorHeading';

const BankErrorSubHeading = styled.p`
  color: ${colors.TEXT_SECONDARY};
  font-size: 1rem;
  font-weight: 400;
`;
BankErrorSubHeading.displayName = 'BankErrorSubHeading';

// Bias the button to the right of the second grid column
const BankErrorDetailsButton = styled(SupplementButton)`
  justify-self: right;
  margin-right: 1.75rem;
  font-weight: 600;
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin-right: 0;
  }
`;
BankErrorDetailsButton.displayName = 'BankErrorDetailsButton';

const StyledHeading = styled(Heading)`
  max-width: 20rem;
  margin: 0 auto;
  line-height: 2rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    max-width: 18.25rem;
    margin: 0 auto 0.4375rem;
  }
`;
StyledHeading.displayName = 'StyledHeading';

const StyledSubHeading = styled(SubHeading)`
  max-width: 23.75rem;
  margin: 1.0625rem auto 0.9375rem;
  line-height: 1.5rem;
  text-align: left;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0.625rem 0.5rem 1.125rem;
    text-align: left;
  }
`;
StyledSubHeading.displayName = 'StyledSubHeading';

const StyledSecondarySubHeading = styled(StyledSubHeading)`
  max-width: 23.75rem;
  margin: 0 auto 1rem;
  line-height: 1.5rem;
  font-size: 1rem;
  text-align: left;
  margin-left: auto;
  b {
    font-weight: 700;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0.5rem 0.5rem 1.375rem;
  }
`;
StyledSecondarySubHeading.displayName = 'StyledSecondarySubHeading';

const FeatureList = styled.div`
  margin: 0 auto 1rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 auto 0.625rem;
  }
`;
FeatureList.displayName = 'FeatureList';

const FeatureListBullet = styled(FeatureListItemBullet)`
  margin: 0 0.75rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 0.75rem 0 0;
  }
`;
FeatureListBullet.displayName = 'FeatureListBullet';

const BankAuthorizationAgreement = styled.p`
  padding: 0.125rem 0 1.375rem;
  font-weight: 400;
  font-size: 0.8125rem;
  line-height: 1.375rem;
  text-align: center;
  width: 25rem;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 100%;
    padding: 0 0.5rem 1.5rem;
    text-align: left;
  }
`;
BankAuthorizationAgreement.displayName = 'BankAuthorizationAgreement';

const BankAuthorizationAgreementUnderlined = styled(FileDownloadLink)`
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.375rem;
`;
BankAuthorizationAgreementUnderlined.displayName =
  'BankAuthorizationAgreementUnderlined';

const PlaidLinkButtonContainer = styled.div`
  margin: 0 0 5.3125rem;
  max-width: 25rem;
  width: 100%;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 0 2.125rem;
    padding: 0;
  }
`;
PlaidLinkButtonContainer.displayName = 'PlaidLinkButtonContainer';

const ShieldIconContainer = styled.div`
  margin: 2.875rem 0 1rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 1.6125rem 0 0.6875rem;
  }
`;
ShieldIconContainer.displayName = 'ShieldIconContainer';

const ShieldIcon = styled(ShieldIconSVG)`
  height: auto;
  width: 3.3125rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 2.375rem;
  }
`;
ShieldIcon.displayName = 'ShieldIcon';

const BoldSpan = styled.span`
  font-weight: bold;
`;
BoldSpan.displayName = 'BoldSpan';
// #endregion

// Subcomponents ----------------------------------------------------------------------------------
const CheckList = () => (
  <FeatureList>
    <FeatureListItem>
      <FeatureListItemBullet />
      <FeatureListItemText>
        A&nbsp;
        <BoldSpan>checking account</BoldSpan>
        &nbsp;in your name
      </FeatureListItemText>
    </FeatureListItem>
    <FeatureListItem>
      <FeatureListItemBullet />
      <FeatureListItemText>A positive balance</FeatureListItemText>
    </FeatureListItem>
    <FeatureListItem>
      <FeatureListItemBullet />
      <FeatureListItemText>Transaction history</FeatureListItemText>
    </FeatureListItem>
  </FeatureList>
);

// Component --------------------------------------------------------------------------------------
export const ConnectToBank = () => {
  // State ----------------------------------------------------------------------------------------
  const [fetchingBankConnectionAuthDoc, setFetchingBankConnectionAuthDoc] =
    useState(false);
  const [showDocumentFetchFailure, setShowDocumentFetchFailure] =
    useState(false);
  const [bcaDocument, setBcaDocument] = useState<BCADocumentType>({
    name: '',
    title: '',
    version: 0,
    viewUrl: '',
  });
  const [authorizedBankConnect, setAuthorizedBankConnect] = useState(false);
  const [consentInProgress, setConsentInProgress] = useState(false);
  const [plaidShouldBeOpen, setPlaidShouldBeOpen] = useState(false);
  const [bankConnectErrorBanner, setBankConnectErrorBanner] = useState({
    display: false,
    institution: null,
  });
  const [displayGenericBankErrorBanner, setDisplayGenericBankErrorBanner] =
    useState(false);
  const [displayBankConnectTipsModal, setDisplayBankConnectTipsModal] =
    useState(false);

  // Context --------------------------------------------------------------------------------------
  const {
    fetchAndResumeCreditCardApplication,
    fetchCreditCardApplication,
    fetchingApplication,
    showApplicationFetchError,
    creditCardApplication,
    showVerificationWaitingRoom,
    setShowVerificationWaitingRoom,
  } = useContext(CreditCardApplicationContext);

  const { setShowErrorModal } = useContext(ErrorContext);

  // Get application ID to send on successful plaid onboarding
  const { applicationId } = useParams();

  // Hooks ----------------------------------------------------------------------------------------
  const navigate = useNavigate();

  // Functions ------------------------------------------------------------------------------------
  // Get legal document for Bank Connection Authorization
  const fetchBankConnectionAuthDocument = async () => {
    // Show spinner
    setFetchingBankConnectionAuthDoc(true);
    try {
      // Fetch legal docs
      const response: GraphQLResponse = await getCreditCardLegalDocuments(
        LegalDocumentContexts.BANK_CONNECTION
      );
      const { data, errors } = response;
      if (data && data.getCreditCardLegalDocuments) {
        setBcaDocument(data.getCreditCardLegalDocuments[0]);
      }
      if (errors) {
        setShowDocumentFetchFailure(true);
      }
    } catch (err: any) {
      setShowDocumentFetchFailure(true);
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'ConnectToBank',
          function: 'fetchBankConnectionAuthDocument',
        });
      }
    } finally {
      // Always turn fetchingBankConnectionAuthDoc off
      setFetchingBankConnectionAuthDoc(false);
    }
  };

  // Process workflow event 'BANK_CONNECT_AUTHORIZED' when the user clicks 'Sign in to my bank'
  const processWorkflowEventBankConnectAuthorized = async () => {
    // Skip when workflow event BANK_CONNECT_AUTHORIZED has already been processed previously
    if (authorizedBankConnect) return;
    setConsentInProgress(true);
    try {
      const workflowEvent = ApplicationWorkflowEvent.BANK_CONNECT_AUTHORIZED;
      const workflowEventAttributes = {
        legalDocumentsToSave: [
          {
            name: bcaDocument.name,
            version: bcaDocument.version,
          },
        ],
      };
      const response = await processCreditCardApplicationWorkflowEvent(
        applicationId,
        workflowEvent,
        workflowEventAttributes
      );
      const { data, errors } = response;
      nrTrackCreditCardApplicationWorkflowEvent(data);
      if (!errors && data && data.processCreditCardApplicationWorkflowEvent) {
        // Update credit card application in context after successful workflow event
        fetchCreditCardApplication();
        // Automatically open Plaid after BCA legal doc is saved to the application
        setPlaidShouldBeOpen(true);
      } else if (errors && errors.length > 0 && errors[0].code) {
        // Check if GQL error indicates BANK_CONNECT_AUTHORIZED already happened
        switch (errors[0].code) {
          case GraphQLErrorCodes.INVALID_WORKFLOW_EVENT_FOR_STATUS:
            if (/PENDING_BANK_CONNECT/.test(errors[0].message)) {
              // Refresh context application
              fetchCreditCardApplication();
            } else {
              // App resume for any other statuses
              fetchAndResumeCreditCardApplication(PageNames.BANK_CONNECT);
            }
            break;
          case GraphQLErrorCodes.TERMINAL_APPLICATION:
            // App resume to terminal funnel page
            fetchAndResumeCreditCardApplication(PageNames.BANK_CONNECT);
            break;
          default:
            // Unknown error code, just show error
            setShowErrorModal(true);
        }
      } else {
        setShowErrorModal(true);
      }
    } catch (err: any) {
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        setShowErrorModal(true);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'ConnectToBank',
          function: 'processWorkflowEventBankConnectAuthorized',
        });
      }
    } finally {
      setConsentInProgress(false);
    }
  };

  // Process workflow event 'BANK_CONNECTED' when the user successfully completes Plaid onboarding
  const processWorkflowEventBankConnected = async (
    publicToken: string,
    accountId: string
  ) => {
    try {
      const workflowEvent = ApplicationWorkflowEvent.BANK_CONNECTED;
      const workflowEventAttributes = {
        plaidPublicToken: publicToken,
        plaidSelectedBankAccountId: accountId,
      };
      const response = await processCreditCardApplicationWorkflowEvent(
        applicationId,
        workflowEvent,
        workflowEventAttributes
      );
      const { data } = response;
      nrTrackCreditCardApplicationWorkflowEvent(data);
      if (
        data &&
        data.processCreditCardApplicationWorkflowEvent &&
        data.processCreditCardApplicationWorkflowEvent.applicationStatus
      ) {
        // Make sure the status is VERIFICATION_IN_PROGRESS if request was successful
        const { applicationStatus } =
          data.processCreditCardApplicationWorkflowEvent;
        if (applicationStatus === ApplicationStatus.VERIFICATION_IN_PROGRESS) {
          // Allow VerificationWaitingRoom to be shown by setting context
          setShowVerificationWaitingRoom(true);
        } else {
          setShowErrorModal(true);
        }
      } else {
        setShowErrorModal(true);
      }
    } catch (err: any) {
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        setShowErrorModal(true);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'ConnectToBank',
          function: 'processWorkflowEventBankConnected',
        });
      }
    }
  };

  // Clicking 'Sign in to my bank' saves the Bank Connection Authorization document to the app
  const handleConsentLink = () => {
    // Save BCA legal doc if this is the first time the user pressed this button
    if (!authorizedBankConnect) {
      processWorkflowEventBankConnectAuthorized();
    }
  };

  const handleCloseLink = () => {
    window.scrollTo(0, 0);
    // Show the generic bank connect error banner (only)
    setBankConnectErrorBanner({ display: false, institution: '' });
    setDisplayGenericBankErrorBanner(true);
    // Handle user exiting the Plaid widget at the OAuth reinitialization step
    if (/oauth_state_id/.test(window.location.search)) {
      // Remove OAuth query parameter to restart from initial step of choosing an institution
      navigate(window.location.pathname, { replace: true });
    }
    // Always set false after an exit to avoid automatically opening a new Link iFrame
    setPlaidShouldBeOpen(false);
  };

  // Callback when user successfully connects bank thru Plaid Link
  const handleSuccessLink = (publicToken: string, accountId: string) => {
    processWorkflowEventBankConnected(publicToken, accountId);
  };

  const handleClickBankErrorDetailsButton = () => {
    setDisplayBankConnectTipsModal(true);
  };

  useEffect(() => {
    fetchAndResumeCreditCardApplication(PageNames.BANK_CONNECT);
    fetchBankConnectionAuthDocument();
  }, []);

  useEffect(() => {
    if (
      !fetchingApplication &&
      creditCardApplication &&
      creditCardApplication.applicationStatus &&
      creditCardApplication.applicationStatus ===
        ApplicationStatus.PENDING_BANK_CONNECT
    ) {
      setAuthorizedBankConnect(true);
    }
  }, [fetchingApplication]);

  useEffect(() => {
    // Show bank connect error banner if prior connection failed during verification
    if (
      creditCardApplication.applicationStatus ===
        ApplicationStatus.PENDING_BANK_CONNECT &&
      creditCardApplication.selectedBankAccount?.bankName
    ) {
      setBankConnectErrorBanner({
        display: true,
        institution: creditCardApplication.selectedBankAccount?.bankName,
      });
    }
  }, [creditCardApplication]);

  // Route to the VerificationWaitingRoom upon successful bank connection
  useEffect(() => {
    if (showVerificationWaitingRoom) {
      // Route to VerificationWaitingRoom
      navigate(
        prefixRoute(`/application/${applicationId}/verification-waiting-room`)
      );
    }
  }, [showVerificationWaitingRoom]);

  // Render ---------------------------------------------------------------------------------------
  if (
    (fetchingBankConnectionAuthDoc || fetchingApplication) &&
    !plaidShouldBeOpen
  ) {
    return <LoadingCard />;
  }

  if (showDocumentFetchFailure || showApplicationFetchError) {
    return <ErrorCard />;
  }

  return (
    <>
      <BankConnectTipsModal
        showModal={displayBankConnectTipsModal}
        setShowModal={setDisplayBankConnectTipsModal}
      />
      {bankConnectErrorBanner.display && (
        <PlaidErrorCard>
          <PlaidErrorContainer>
            <PlaidErrorHeading>
              Unfortunately, Plaid is unable to connect to this&nbsp;
              {bankConnectErrorBanner.institution ?? 'bank'}
              &nbsp;account. Are you able to try a different account at this
              bank or another?
            </PlaidErrorHeading>
          </PlaidErrorContainer>
        </PlaidErrorCard>
      )}
      {displayGenericBankErrorBanner && (
        <BankErrorCard>
          <BankErrorContainer>
            <BankErrorHeading>
              Having trouble connecting to your bank?
            </BankErrorHeading>
            <BankErrorSubHeading>
              Here are some tips to get you unstuck
            </BankErrorSubHeading>
          </BankErrorContainer>
          <BankErrorDetailsButton
            id="bank-error-details-button"
            aria-label="Show More Details On Connecting Your Bank"
            type="button"
            onClick={handleClickBankErrorDetailsButton}
          >
            See details
          </BankErrorDetailsButton>
        </BankErrorCard>
      )}
      <Card>
        <>
          <ShieldIconContainer id="shield-icon">
            <ShieldIcon />
          </ShieldIconContainer>
          <StyledHeading>
            Connect your bank through our partner Plaid
          </StyledHeading>
          <StyledSubHeading>
            To move your application forward, use Plaid to safely and securely
            provide the following details:
          </StyledSubHeading>
          <CheckList />
          <StyledSecondarySubHeading>
            Rest assured that&nbsp;
            <b>we won’t deduct money </b>
            during this process and our 256-bit encryption keeps your data safe.
          </StyledSecondarySubHeading>
          <BankAuthorizationAgreement>
            By continuing, you agree to our&nbsp;
            <BankAuthorizationAgreementUnderlined
              id="bank-connection-authorization"
              target="_blank"
              href={bcaDocument.viewUrl}
              aria-label={`View document ${bcaDocument.title}`}
            >
              {bcaDocument.title}
            </BankAuthorizationAgreementUnderlined>
          </BankAuthorizationAgreement>
          <PlaidLinkButtonContainer>
            <PlaidLink
              onSuccess={handleSuccessLink}
              onConsent={handleConsentLink}
              onExit={handleCloseLink}
              onOpenClick={() => setPlaidShouldBeOpen(true)}
              plaidShouldBeOpen={plaidShouldBeOpen}
              authorized={authorizedBankConnect}
              consentInProgress={consentInProgress}
            >
              Connect my bank
            </PlaidLink>
          </PlaidLinkButtonContainer>
        </>
      </Card>
    </>
  );
};
